import { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/components/layout/ThemeProvider';
import { AppShell } from '@/components/layout/AppShell';
import { useAuthStore } from '@/lib/auth';
import { useThemeStore } from '@/lib/stores';

// Pages
import LandingPage from '@/pages/public/LandingPage';
import FeaturesPage from '@/pages/public/FeaturesPage';
import PricingPage from '@/pages/public/PricingPage';
import AboutPage from '@/pages/public/AboutPage';
import ArchitecturePage from '@/pages/public/ArchitecturePage';
import { BlogListPage, BlogPostPage } from '@/pages/public/BlogPage';
import JoinPage from '@/pages/public/JoinPage';
import WhitepaperPage from '@/pages/public/WhitepaperPage';
import PublicLayout from '@/components/layout/PublicLayout';
import LoginPage from '@/pages/auth/LoginPage';
import SignupPage from '@/pages/auth/SignupPage';
import DashboardPage from '@/pages/app/DashboardPage';
import ProjectsPage from '@/pages/app/ProjectsPage';
import ProjectWorkspace from '@/pages/app/ProjectWorkspace';
import TemplatesPage from '@/pages/app/TemplatesPage';
import SettingsPage from '@/pages/app/SettingsPage';
import GuidancesPage from '@/pages/app/GuidancesPage';
import AdminDashboard, { AdminLayout } from '@/pages/admin/AdminDashboard';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminTemplatesPage from '@/pages/admin/AdminTemplatesPage';
import AdminAnalyticsPage from '@/pages/admin/AdminAnalyticsPage';
import AdminTeamPage from '@/pages/admin/AdminTeamPage';
import AdminConfigPage from '@/pages/admin/AdminConfigPage';
import AdminActivityPage from '@/pages/admin/AdminActivityPage';
import AdminFeedbackPage from '@/pages/admin/AdminFeedbackPage';
import AdminEmailPage from '@/pages/admin/AdminEmailPage';
import AdminWaitlistPage from '@/pages/admin/AdminWaitlistPage';
import AdminAccountPage from '@/pages/admin/AdminAccountPage';
import FeedbackWidget from '@/components/FeedbackWidget';

// Auth guard
function ProtectedRoute({ children }) {
  const { user, loading } = useAuthStore();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Admin guard - requires superadmin role
function AdminRoute({ children }) {
  const { user, loading } = useAuthStore();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Access Denied</h1>
          <p className="text-muted-foreground text-sm">SuperAdmin access required</p>
          <a href="/app" className="text-primary text-sm mt-4 inline-block hover:underline">Back to Dashboard</a>
        </div>
      </div>
    );
  }
  
  return children;
}

// Guidances placeholder
// GuidancesPage imported from pages/app/GuidancesPage.jsx

function App() {
  const { init, user } = useAuthStore();
  const { theme } = useThemeStore();

  useEffect(() => {
    init();
    // Set initial theme
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          {/* Public - with shared layout */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/architecture" element={<ArchitecturePage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/whitepaper" element={<WhitepaperPage />} />
          </Route>

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />

          {/* App (Protected) */}
          <Route path="/app" element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }>
            <Route index element={<DashboardPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="project/:id" element={<ProjectWorkspace />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="guidances" element={<GuidancesPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="help" element={<div className="p-8"><h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Help & Documentation</h1></div>} />
          </Route>

          {/* Admin (Protected + SuperAdmin) */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="templates" element={<AdminTemplatesPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="activity" element={<AdminActivityPage />} />
            <Route path="feedback" element={<AdminFeedbackPage />} />
            <Route path="email" element={<AdminEmailPage />} />
            <Route path="waitlist" element={<AdminWaitlistPage />} />
            <Route path="team" element={<AdminTeamPage />} />
            <Route path="config" element={<AdminConfigPage />} />
            <Route path="account" element={<AdminAccountPage />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
      {user && <FeedbackWidget />}
    </ThemeProvider>
  );
}

export default App;
